import config from 'Globals/constants'
import { getLatestUpdated } from 'Src/redux/reservation/guests/guests.utils'
import { BusinessLeadState } from 'Src/redux/business-lead/business-lead.reducer'
import { selectDurationOfStay } from 'Src/redux/reservation/times/times.selectors'
import { RootState } from 'Src/redux/store'
import { selectReservation, selectReservationState } from 'Src/redux/reservation/reservation.selectors'
import { ReservationState, EnrichedProduct } from 'Src/types/reservation-state.types'
import { Customer, ReservationApi } from 'Src/types/api.types'
import { UserTraits } from '@segment/analytics-next'
import { getTotalStayDuration } from 'Src/redux/reservation/times/times.utils'
import { AnalyticsProduct } from 'Src/utils/analytics/analytics'
import { analyticsBrand } from 'Src/globals/constants'

const { STEPS, COUNTRIES } = config

const { CHECK_IN } = STEPS

interface StepEventData {
	readonly state: RootState
	readonly businessLead: BusinessLeadState | null
	readonly durationOfStay: number
	readonly customerId: string | null
}

export const stepNames = {
	[CHECK_IN.MAIN['start']]: 'Check-in Start',
	[CHECK_IN.MAIN['main-guest-contacts']]: 'Main Guest Contact Information',
	[CHECK_IN.MAIN['business-lead']]: 'Business Lead',
	[CHECK_IN.SUB['business-lead-form']]: 'Business Lead Form',
	[CHECK_IN.MAIN['main-guest-identification']]: 'Main Guest Identification',
	[CHECK_IN.MAIN['guests']]: 'Guests',
	[CHECK_IN.SUB['main-guest-details']]: 'Main Guest Details',
	[CHECK_IN.SUB['additional-guest-details']]: 'Additional Guest Details',
	[CHECK_IN.MAIN['times']]: 'Arrival and departure time',
	[CHECK_IN.MAIN['summary']]: 'Checkout',
	[CHECK_IN.SUB['add-card']]: 'Add Card',
	[CHECK_IN.COMPLETED['success']]: 'Check-in Completed',
}

export const nextStepNamesToIgnore = [
	CHECK_IN.MAIN['summary'],
	CHECK_IN.MAIN['products'],
	CHECK_IN.SUB['product-parking-indoor'],
	CHECK_IN.SUB['product-breakfast'],
	CHECK_IN.SUB['product-sauna-kit'],
	CHECK_IN.SUB['product-mealkit-platform-pro'],
	CHECK_IN.SUB['product-meal-kit-clean-kitchen-vegan-dan-dan-noodles-2'],
	CHECK_IN.SUB['product-stocked-fridge'],
	CHECK_IN.MAIN['room-upgrade'],
	CHECK_IN.SUB['product-property-unit-category-upgrade'],
	CHECK_IN.SUB['product-property-unit-category-upgrade-free'],
	CHECK_IN.SUB['guest-area-cleaning-mid-stay-intro'],
	CHECK_IN.SUB['guest-area-cleaning-mid-stay-your-schedule'],
	CHECK_IN.SUB['guest-area-cleaning-mid-stay-select-time'],
	CHECK_IN.SUB['guest-area-cleaning-mid-stay-confirm'],
]

export const allowedProductTypes = [
	'breakfast',
	'parking-indoor',
	'parking',
	'sauna-kit',
	'baby-crib',
	'gym',
	'cleaning-mid-stay',
]

export function getProductCategoryAndName(produtType: string): string {
	switch (produtType) {
		case 'breakfast':
			return 'Breakfast'
		case 'parking-indoor':
		case 'parking':
			return 'Parking'
		case 'sauna-kit':
			return 'Sauna Kit'
		case 'baby-crib':
			return 'Baby Crib'
		case 'gym':
			return 'Gym'
		case 'cleaning-mid-stay':
			return 'Mid-Stay Cleaning'
		default:
			return 'Unknown Product'
	}
}

export function getProductAnalyticsPayload(product: EnrichedProduct, currency: string = 'EUR'): AnalyticsProduct {
	const nameAndCategory = getProductCategoryAndName(product.productType ?? '')
	const payload: AnalyticsProduct = {
		name: nameAndCategory,
		product_id: product.productId,
		price: product.price ?? 0,
		brand: analyticsBrand,
		category: nameAndCategory,
		quantity: 1,
		currency,
	}

	return payload
}

function getCountryName(countryCode: string): string {
	const country = COUNTRIES.find((country) => country.code === countryCode)
	return country?.name ?? ''
}

const getMainGuestContactsPayloadData = (stepData: RootState): Record<string, unknown> | undefined => {
	const reservationState = selectReservationState(stepData)
	const { travelReasons, mainGuestCustomer } = reservationState

	const reasons = travelReasons?.map((reason: { label: string }) => reason.label) ?? []

	return {
		type_of_trip: reasons,
		phone: mainGuestCustomer.phone,
	}
}

const getMainGuestPayloadData = (stepData: RootState): Record<string, unknown> | undefined => {
	const reservationState = selectReservationState(stepData)
	const { mainGuestCustomer } = reservationState

	const countryOfResidenceName = getCountryName(mainGuestCustomer.customerAddress?.countryCode ?? '')
	const citizenshipName = getCountryName(mainGuestCustomer.nationalityCode ?? '')
	const dateOfBirth = mainGuestCustomer.birthDate?.split('T')[0]

	const payload: Record<string, unknown> = {
		first_name: mainGuestCustomer.firstName,
		last_name: mainGuestCustomer.lastName,
		citizenship: citizenshipName,
		birthday: dateOfBirth,
		country_of_residence: countryOfResidenceName,
		address: mainGuestCustomer.customerAddress?.addressLine1 ?? '',
	}

	if (mainGuestCustomer.customerDocuments?.length > 0) {
		const latestDocument = getLatestUpdated(mainGuestCustomer.customerDocuments)

		payload['document_type'] = latestDocument?.type
	}

	return payload
}

const getAdditionalGuestPayloadData = (
	stepData: RootState,
	customerId: string | null,
): Record<string, unknown> | undefined => {
	const reservationState = selectReservationState(stepData)
	const { additionalGuests } = reservationState

	let additionalGuest = additionalGuests?.find((guest) => guest.id === customerId)

	if (!additionalGuest) {
		additionalGuest = additionalGuests?.[0]
	}

	const citizenshipName = getCountryName(additionalGuest?.customer?.nationalityCode ?? '')
	const dateOfBirth = additionalGuest?.customer?.birthDate?.split('T')[0]

	return {
		first_name: additionalGuest?.customer?.firstName,
		last_name: additionalGuest?.customer?.lastName,
		citizenship: citizenshipName,
		birthday: dateOfBirth,
		phone: additionalGuest?.customer?.phone ?? '',
		email: additionalGuest?.customer?.email ?? '',
	}
}

const getBusinessLeadPayloadData = (businessLead: BusinessLeadState): Record<string, unknown> | undefined => {
	return {
		company_name: businessLead?.companyName ?? '',
		company_size: businessLead?.companySizeRange ?? '',
		contact_person: businessLead?.contactPerson ?? '',
		contact_email: businessLead?.contactEmail ?? '',
		contact_job_title: businessLead?.contactJobTitle ?? '',
	}
}

export const getSuccessPayloadData = (state: RootState): Record<string, unknown> | undefined => {
	const durationOfStay = selectDurationOfStay(state)
	const reservation = selectReservation(state)
	const property_unit_category_name = reservation?.requestedPropertyUnitCategory?.name ?? ''
	const property_name = reservation?.property?.name ?? ''
	const city = reservation?.property?.city ?? ''
	const start_date = reservation?.startDate ?? ''
	const end_date = reservation?.endDate ?? ''
	const adult_count = reservation?.adultCount ?? ''
	const child_count = reservation?.childrenCount ?? ''
	const channel = reservation?.travelCompanyId ? 'OTA' : 'direct'

	return {
		property_unit_category_name,
		property_name,
		city,
		start_date,
		end_date,
		adult_count,
		child_count,
		duration: durationOfStay,
		channel,
	}
}

function getStepPayloadData(
	step: number, data: StepEventData,
): Record<string, unknown> | undefined {
	const { state, customerId } = data

	switch (step) {
		case CHECK_IN.MAIN['main-guest-contacts']:
			return getMainGuestContactsPayloadData(state)

		case CHECK_IN.MAIN['main-guest-identification']:
		case CHECK_IN.SUB['main-guest-details']:
			return getMainGuestPayloadData(state)

		case CHECK_IN.SUB['additional-guest-details']:
			return getAdditionalGuestPayloadData(state, customerId)

		case CHECK_IN.SUB['business-lead-form']:
			return getBusinessLeadPayloadData(data.businessLead)

		case CHECK_IN.COMPLETED['success']:
			return getSuccessPayloadData(state)

		case CHECK_IN.MAIN['start']:
		default:
			return undefined
	}
}

export const getStepEventData = (step: number, data: StepEventData) => {
	const stepName = stepNames[step]
	const stepPayload = getStepPayloadData(step, data)
	const isMarketingConsentGiven = data.state?.reservationState?.customer?.isMarketingConsentGiven ?? false

	return {
		step_name: stepName,
		reservation_id: data.state?.reservationState?.reservation?.id,
		is_marketing_consent_given: isMarketingConsentGiven,
		...stepPayload,
	}
}

const getCustomerTraits = (customer: Customer) => {
	const citizenshipName = customer?.nationalityCode ? getCountryName(customer.nationalityCode) : undefined
	const dateOfBirth = customer?.birthDate?.split('T')[0] ?? undefined
	const country = customer?.customerAddress?.countryCode
		? getCountryName(customer.customerAddress?.countryCode)
		: undefined

	const traits: UserTraits = {
		email: customer?.email ?? undefined,
		phone: customer?.phone ?? undefined,
		first_name: customer?.firstName ?? undefined,
		last_name: customer?.lastName ?? undefined,
		citizenship: citizenshipName ?? undefined,
		birthday: dateOfBirth ?? undefined,
		address: {
			street: customer?.customerAddress?.addressLine1 ?? undefined,
			city: customer?.customerAddress?.city ?? undefined,
			country,
			postalCode: customer?.customerAddress?.postalCode ?? undefined,
		},
		gender: customer?.gender ?? undefined,
	}

	return traits
}

export const getIdentifyStepPayloadData = (reservation: ReservationState) => {
	const { mainGuestCustomer } = reservation
	const traits = getCustomerTraits(mainGuestCustomer)

	return {
		id: mainGuestCustomer.id,
		traits,
	}
}

export const getIdentifyPayloadData = (reservation: ReservationApi) => {
	const { customer } = reservation
	const traits = getCustomerTraits(customer)

	return {
		id: customer.id,
		traits,
	}
}

export const getGroupTraitsData = (reservation: ReservationApi) => {
	const reservationStartDate = reservation?.startDate?.split('T')[0] ?? null
	const reservationEndDate = reservation?.endDate?.split('T')[0] ?? null

	return {
		adult_count: reservation.adultCount,
		child_count: reservation.childrenCount,
		stay_duration: getTotalStayDuration(reservation),
		start_date: reservationStartDate,
		end_date: reservationEndDate,
		confirmation_number: reservation.confirmationNumber,
		group: 'reservation',
	}
}
